import {
  Box,
  Button,
  Divider,
  Drawer,
  Flex,
  MultiSelect,
  Select,
  Text,
} from "@mantine/core";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../../model/MessageResponse";

export type ReportScopeTarget = {
  empId: number;
  empCode: string;
  empName: string;
  /** Có mã báo cáo là đang sửa quyền sẵn có, không có là đang cấp thêm báo cáo mới */
  code?: string;
  name?: string;
  listBranchId?: string;
  listDepartmentId?: string;
  /** Những báo cáo người này đã được cấp, để lúc thêm mới không chọn trùng */
  grantedCodes?: string[];
};

type Props = {
  target: ReportScopeTarget | null;
  dataBranch: any[];
  dataDepartment: any[];
  onClose: () => void;
  onSaved: () => void;
};

/** Tách chuỗi mã ngăn phẩy thành mảng, bỏ rỗng và bỏ mục "Tất cả" */
const splitIds = (value?: string) =>
  (value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0 && item !== "-1");

/**
 * Ngăn kéo sửa phạm vi của một quyền đọc báo cáo.
 *
 * Phạm vi được cấp riêng cho từng cặp nhân viên - báo cáo, nên mỗi lần mở
 * chỉ làm việc với đúng một cặp. Cùng một ngăn kéo dùng cho cả sửa lẫn cấp mới,
 * khác nhau ở chỗ cấp mới thì phải chọn báo cáo trước.
 */
const ReportScopeDrawer = ({
  target,
  dataBranch,
  dataDepartment,
  onClose,
  onSaved,
}: Props) => {
  const isEdit = !!target?.code;

  const [apiKey, setApiKey] = useState<string | null>(null);
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [dataReport, setDataReport] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!target) return;

    setApiKey(target.code ?? null);
    setBranchIds(splitIds(target.listBranchId));
    setDepartmentIds(splitIds(target.listDepartmentId));
  }, [target]);

  useEffect(() => {
    // Danh mục báo cáo chỉ cần lấy một lần, dùng lại cho mọi lần mở ngăn kéo
    if (dataReport.length > 0) return;

    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const fetchReport = async () => {
    try {
      const res = await repositoryPos.get<MessageResponse<any[]>>(
        "/api/v1/AuthReportReading/get-list-auth-list-report?Skip=0&Take=1000",
      );

      if (res && res.success) {
        const list = res.data
          ?.filter((item: any) => item.code)
          ?.map((item: any) => ({
            value: item.code?.toString(),
            label: item.name?.toString() || item.code?.toString(),
          }));
        setDataReport(list ?? []);
      }
    } catch (error) {
      console.error("Không lấy được danh mục báo cáo", error);
    }
  };

  /**
   * Chọn "Tất cả" thì đổi thành chọn hết từng mục thật.
   * Lưu xuống là chuỗi mã nên phải là mã thật, giữ "-1" thì lúc đọc lại không tra ra tên.
   */
  const handlePickAll = (
    picked: string[],
    source: any[],
    setValue: (value: string[]) => void,
  ) => {
    if (picked?.includes("-1")) {
      setValue(
        source
          ?.filter((item) => item.value !== "-1")
          ?.map((item) => item.value),
      );
      return;
    }

    setValue(picked ?? []);
  };

  const handleSave = async () => {
    if (!target) return;

    if (!apiKey) {
      NotificationExtension.Fails("Chưa chọn báo cáo cần cấp quyền !");
      return;
    }

    setIsSaving(true);
    try {
      const url = isEdit
        ? "/api/v1/AuthReportReading/edit-auth-reading"
        : "/api/v1/AuthReportReading/add-auth-reading";

      const res = await repositoryPos.post<MessageResponse<any>>(url, {
        id: "0",
        empId: target.empId,
        apiKey: apiKey,
        listBranchId: branchIds.join(","),
        listDepartmentId: departmentIds.join(","),
        typeRole: null,
        attribute1: null,
        attribute2: null,
        attribute3: null,
        attribute4: null,
        attribute5: null,
        attribute6: null,
        attribute7: null,
        attribute8: null,
        attribute9: null,
        attribute10: null,
        createDate: null,
        createBy: null,
        lastUpdateDate: null,
        lastUpdateBy: null,
      });

      if (res && res.success) {
        NotificationExtension.Success(
          isEdit ? "Cập nhật phạm vi thành công !" : "Cấp quyền thành công !",
        );
        onSaved();
        onClose();
      } else {
        NotificationExtension.Fails(res?.message ?? "Lưu không thành công !");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi khi lưu phạm vi !");
    } finally {
      setIsSaving(false);
    }
  };

  // Cấp mới thì bỏ đi những báo cáo người này đã có, tránh lưu xong mới báo trùng
  const reportOptions = isEdit
    ? dataReport
    : dataReport.filter(
        (item) => !(target?.grantedCodes ?? []).includes(item.value),
      );

  return (
    <Drawer
      opened={!!target}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      title={
        <Box>
          <Text fw={600} size="sm">
            {isEdit ? "Sửa phạm vi báo cáo" : "Cấp thêm báo cáo"}
          </Text>
          <Text size="xs" c="dimmed">
            {target?.empCode} - {target?.empName}
          </Text>
        </Box>
      }
    >
      <Divider mb="md" />

      <Select
        label="Báo cáo"
        placeholder="Chọn báo cáo"
        data={reportOptions}
        value={apiKey}
        onChange={setApiKey}
        searchable
        // Sửa thì báo cáo là cố định, đổi báo cáo là một quyền khác chứ không phải sửa quyền này
        disabled={isEdit}
        nothingFoundMessage="Không còn báo cáo nào để cấp !"
        mb="md"
      />

      <MultiSelect
        label="Danh sách chi nhánh"
        placeholder="Chọn chi nhánh"
        data={dataBranch}
        value={branchIds}
        onChange={(e) => handlePickAll(e, dataBranch, setBranchIds)}
        searchable
        clearable
        hidePickedOptions
        nothingFoundMessage="Không tìm thấy chi nhánh !"
        mb="md"
      />

      <MultiSelect
        label="Danh sách phòng ban"
        placeholder="Chọn phòng ban"
        data={dataDepartment}
        value={departmentIds}
        onChange={(e) => handlePickAll(e, dataDepartment, setDepartmentIds)}
        searchable
        clearable
        hidePickedOptions
        nothingFoundMessage="Không tìm thấy phòng ban !"
        mb="md"
      />

      <Divider my="md" />

      <Flex gap="xs" justify="flex-end">
        <Button
          variant="outline"
          color="gray"
          leftSection={<IconX size={16} />}
          onClick={onClose}
        >
          Đóng
        </Button>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          loading={isSaving}
          onClick={handleSave}
        >
          Lưu
        </Button>
      </Flex>
    </Drawer>
  );
};

export default ReportScopeDrawer;
